"use client";

import clsx from "clsx";
import type { GraphState, NodeState } from "./types";
import { BankBadge } from "@/components/banks/BankBadge";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * NetworkVignette — compact SVG mini-graph for inline rendering inside
 * scenes. Replaces the persistent WebRail with curated, scene-specific
 * snapshots that show only the nodes/edges relevant to the current beat.
 *
 *   preset = "orbid-mint"        → Aurora ↔ Al Madar (newly verified) + GLEIF
 *   preset = "doc-attestation"   → Aurora ↔ Al Madar with 3 attestor nodes + HCS diamonds on the edge
 *   preset = "lc-self-financed"  → Aurora ↔ Al Madar with LC node, no bank
 *   preset = "lc-fab-financed"   → Aurora ↔ Al Madar with LC + FAB bank node
 *   preset = "lc-itau-financed"  → Pampagro ↔ Gulf Phosphate with LC + Itaú bank
 *
 * Reads the live `graph` for actual node states (cold/verified/traced/
 * financed) so the visual reflects what's already happened. Hand-positioned
 * layout — no force simulation, no jitter, predictable composition.
 */

export type Preset =
  | "orbid-mint"
  | "doc-attestation"
  | "lc-self-financed"
  | "lc-fab-financed"
  | "lc-itau-financed";

/**
 * NetworkVignette is SVG, not Canvas — so it must consume CSS custom
 * properties via var(...) like the rest of the build, not raw hex literals
 * (Critical Design Principle 10). The InterconnectedWeb canvas is the only
 * documented exception (canvas ctx APIs need raw hex).
 */
const COLOURS: Record<NodeState, { fill: string; stroke: string }> = {
  cold:     { fill: "var(--state-cold)", stroke: "var(--state-cold)" },
  verified: { fill: "var(--orbitra-ink)", stroke: "var(--state-verified)" },
  traced:   { fill: "var(--orbitra-ink)", stroke: "var(--state-traced)" },
  financed: { fill: "var(--orbitra-ink)", stroke: "var(--state-financed)" },
};

const EDGE_COLOUR: Record<NodeState, string> = {
  cold:     "var(--state-cold-edge)",
  verified: "var(--state-verified-edge)",
  traced:   "var(--state-traced-edge)",
  financed: "var(--state-financed-edge)",
};

type PositionedNode = {
  orbid: string;
  cx: number;
  cy: number;
  initials: string;
  flag?: string;
  fallbackState: NodeState;
  pulse?: boolean;
};

type PositionedEdge = {
  from: string;
  to: string;
  fallbackState: NodeState;
  diamonds?: number;
  bankBadge?: "FAB" | "Itau";
  bankLabel?: string;
};

const VIEWBOX_W = 520;
const VIEWBOX_H = 130;

function presetLayout(preset: Preset): { nodes: PositionedNode[]; edges: PositionedEdge[]; title: string } {
  switch (preset) {
    case "orbid-mint":
      return {
        title: "Network impact · OrbID minted",
        nodes: [
          { orbid: "orbid:5493-aves-aurora-br", cx: 120, cy: 78, initials: "AA", flag: "BR", fallbackState: "verified" },
          { orbid: "orbid:5493-almadar-ae",     cx: 400, cy: 78, initials: "AM", flag: "AE", fallbackState: "verified", pulse: true },
          { orbid: "gleif",                      cx: 260, cy: 30, initials: "GLEIF", fallbackState: "verified", pulse: true },
        ],
        edges: [
          { from: "orbid:5493-aves-aurora-br", to: "orbid:5493-almadar-ae", fallbackState: "verified" },
          { from: "gleif",                      to: "orbid:5493-almadar-ae", fallbackState: "verified" },
        ],
      };

    case "doc-attestation":
      return {
        title: "Network impact · documents anchored on Orbitra",
        nodes: [
          { orbid: "orbid:5493-aves-aurora-br", cx: 100, cy: 78, initials: "AA", flag: "BR", fallbackState: "traced" },
          { orbid: "orbid:5493-almadar-ae",     cx: 420, cy: 78, initials: "AM", flag: "AE", fallbackState: "traced" },
          { orbid: "orbid:cdial-halal-br",      cx: 60,  cy: 28, initials: "CD", flag: "BR", fallbackState: "traced", pulse: true },
          { orbid: "orbid:abcc-cert-br",        cx: 140, cy: 28, initials: "AB", flag: "BR", fallbackState: "traced", pulse: true },
          { orbid: "orbid:mapa-export-br",      cx: 220, cy: 28, initials: "MA", flag: "BR", fallbackState: "traced", pulse: true },
        ],
        edges: [
          { from: "orbid:5493-aves-aurora-br", to: "orbid:5493-almadar-ae", fallbackState: "traced", diamonds: 4 },
          { from: "orbid:5493-aves-aurora-br", to: "orbid:cdial-halal-br", fallbackState: "traced" },
          { from: "orbid:5493-aves-aurora-br", to: "orbid:abcc-cert-br",   fallbackState: "traced" },
          { from: "orbid:5493-aves-aurora-br", to: "orbid:mapa-export-br", fallbackState: "traced" },
        ],
      };

    case "lc-self-financed":
      return {
        title: "Network impact · Programmable Escrow · 100% Buyer funds",
        nodes: [
          { orbid: "orbid:5493-aves-aurora-br", cx: 100, cy: 78, initials: "AA", flag: "BR", fallbackState: "financed" },
          { orbid: "orbid:5493-almadar-ae",     cx: 420, cy: 78, initials: "AM", flag: "AE", fallbackState: "financed" },
          { orbid: "lc-2026-00042",             cx: 260, cy: 78, initials: "OE", fallbackState: "financed", pulse: true },
        ],
        edges: [
          { from: "orbid:5493-aves-aurora-br", to: "lc-2026-00042",          fallbackState: "financed" },
          { from: "lc-2026-00042",             to: "orbid:5493-almadar-ae", fallbackState: "financed" },
        ],
      };

    case "lc-fab-financed":
      return {
        title: "Network impact · LC issued · 30% FAB partial financing",
        nodes: [
          { orbid: "orbid:5493-aves-aurora-br", cx: 100, cy: 78, initials: "AA", flag: "BR", fallbackState: "financed" },
          { orbid: "orbid:5493-almadar-ae",     cx: 420, cy: 78, initials: "AM", flag: "AE", fallbackState: "financed" },
          { orbid: "lc-2026-00187",             cx: 260, cy: 78, initials: "LC", fallbackState: "financed" },
          { orbid: "bank:FAB",                  cx: 420, cy: 28, initials: "FAB", flag: "AE", fallbackState: "financed", pulse: true },
        ],
        edges: [
          { from: "orbid:5493-aves-aurora-br", to: "lc-2026-00187",         fallbackState: "financed" },
          { from: "lc-2026-00187",             to: "orbid:5493-almadar-ae", fallbackState: "financed", bankBadge: "FAB", bankLabel: "30% FAB" },
          { from: "bank:FAB",                  to: "orbid:5493-almadar-ae", fallbackState: "financed" },
        ],
      };

    case "lc-itau-financed":
      return {
        title: "Network impact · LC issued · 50% Itaú partial financing",
        nodes: [
          { orbid: "orbid:5493-pampagro-br",       cx: 100, cy: 78, initials: "PA", flag: "BR", fallbackState: "financed" },
          { orbid: "orbid:5493-gulfphosphate-ae",  cx: 420, cy: 78, initials: "GP", flag: "AE", fallbackState: "financed" },
          { orbid: "lc-2026-00342",                cx: 260, cy: 78, initials: "LC", fallbackState: "financed" },
          { orbid: "bank:Itau",                    cx: 100, cy: 28, initials: "Itaú", flag: "BR", fallbackState: "financed", pulse: true },
        ],
        edges: [
          { from: "orbid:5493-pampagro-br",  to: "lc-2026-00342",                fallbackState: "financed", bankBadge: "Itau", bankLabel: "50% Itaú" },
          { from: "lc-2026-00342",            to: "orbid:5493-gulfphosphate-ae", fallbackState: "financed" },
          { from: "bank:Itau",                to: "orbid:5493-pampagro-br",       fallbackState: "financed" },
        ],
      };
  }
}

/**
 * Resolve a positioned node's display state — prefer the live graph state
 * if the node exists, fall back to the preset's expected state otherwise.
 */
function resolveState(graph: GraphState, orbid: string, fallback: NodeState): NodeState {
  return graph.nodes.get(orbid)?.state ?? fallback;
}

export function NetworkVignette({
  graph,
  preset,
  className,
}: {
  graph: GraphState;
  preset: Preset;
  className?: string;
}) {
  const tr = useTranslations("networkVignette");
  const layout = presetLayout(preset);
  const nodeById = new Map(layout.nodes.map((n) => [n.orbid, n]));

  return (
    <div className={clsx("glass relative flex shrink-0 flex-col overflow-hidden p-4 min-h-[180px]", className)}>
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-state-traced">{layout.title}</p>
      <svg
        viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
        preserveAspectRatio="xMidYMid meet"
        className="mt-2 h-[140px] w-full"
        role="img"
        aria-label={layout.title}
      >
        {/* Edges first */}
        {layout.edges.map((e) => {
          const a = nodeById.get(e.from);
          const b = nodeById.get(e.to);
          if (!a || !b) return null;
          const stroke = EDGE_COLOUR[resolveState(graph, e.from, e.fallbackState) === "cold" ? e.fallbackState : resolveState(graph, e.from, e.fallbackState)];
          const diamondCount = e.diamonds ?? 0;
          return (
            <g key={`${e.from}->${e.to}`}>
              <line
                x1={a.cx} y1={a.cy} x2={b.cx} y2={b.cy}
                stroke={stroke} strokeWidth={2} opacity={0.95}
              />
              {/* HCS anchor diamonds along the edge */}
              {Array.from({ length: diamondCount }).map((_, i) => {
                const t = (i + 1) / (diamondCount + 1);
                const dx = a.cx + (b.cx - a.cx) * t;
                const dy = a.cy + (b.cy - a.cy) * t;
                const r = 4;
                return (
                  <polygon
                    key={`${e.from}->${e.to}-d${i}`}
                    points={`${dx},${dy - r} ${dx + r},${dy} ${dx},${dy + r} ${dx - r},${dy}`}
                    fill="var(--state-traced)"
                  />
                );
              })}
            </g>
          );
        })}

        {/* Nodes */}
        {layout.nodes.map((n) => {
          const state = resolveState(graph, n.orbid, n.fallbackState);
          const colour = COLOURS[state];
          const r = n.initials.length > 2 ? 14 : 12;
          return (
            <g key={n.orbid}>
              {/* Pulse halo — subtle 18% alpha glow */}
              {n.pulse && (
                <circle cx={n.cx} cy={n.cy} r={r * 1.8} fill={colour.stroke} opacity={0.18}>
                  <animate attributeName="r" from={r * 1.4} to={r * 2.0} dur="1.4s" repeatCount="indefinite" />
                  <animate attributeName="opacity" from="0.28" to="0.08" dur="1.4s" repeatCount="indefinite" />
                </circle>
              )}
              <circle cx={n.cx} cy={n.cy} r={r} fill={colour.fill} stroke={colour.stroke} strokeWidth={1.6} />
              <text
                x={n.cx} y={n.cy + 0.5}
                textAnchor="middle" dominantBaseline="middle"
                fill={state === "cold" ? "var(--orbitra-muted)" : colour.stroke}
                fontFamily="var(--font-display, sans-serif)"
                fontWeight={600}
                fontSize={Math.max(8, r * 0.85)}
              >
                {n.initials}
              </text>
              {/* Jurisdiction flag chip */}
              {n.flag && (
                <g transform={`translate(${n.cx + r * 0.85}, ${n.cy + r * 0.85})`}>
                  <circle r={6} fill="var(--orbitra-ink)" stroke="var(--state-verified-rim)" strokeWidth={0.8} />
                  <text x={0} y={0.3} textAnchor="middle" dominantBaseline="middle"
                        fill="var(--orbitra-teal-soft)" fontFamily="var(--font-mono, monospace)" fontWeight={600} fontSize={7}>
                    {n.flag}
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </svg>

      {/* Bank badge annotation — rendered as HTML overlay below the svg
       *  for legibility (small SVG text wouldn't scale cleanly across
       *  locales, and BankBadge already handles legal name + jurisdiction). */}
      {layout.edges.find((e) => e.bankBadge) && (
        <div className="mt-2 flex items-center gap-2 text-[10px] text-state-financed">
          {layout.edges.filter((e) => e.bankBadge).map((e) => (
            <BankBadge key={e.bankBadge!} name={e.bankBadge!} variant="compact" />
          ))}
          <span className="font-mono uppercase tracking-wider text-orbitra-muted">
            {tr("buyerSideFinancingPartner")}
          </span>
        </div>
      )}
    </div>
  );
}
